/**
 * ENGLISH - the reference dictionary
 * -----------------------------------
 * This file is the source of truth. Every phrase on the site exists here
 * first, and `it.js` is a translation OF this - so a key missing from Italian
 * falls back to the English written here rather than to a blank or to the key
 * itself. See hooks/useI18n.jsx.
 *
 * HOW TO ADD A PHRASE
 *
 * Put it here, under the screen it belongs to, then add the same key to
 * `it.js`. `npm run check-translations --prefix frontend` lists anything in
 * one file and not the other, which is the only way a half-finished
 * translation gets noticed - a fallback is invisible by design.
 *
 * TWO THINGS THAT WILL BITE
 *
 * 1. **Never translate a value the DATABASE stores.** Chore categories are a
 *    Mongoose enum ('Babysitting', 'Pet Care'); a chore saved under an Italian
 *    category would be invisible to every filter and query. `categories` below
 *    maps the stored English value to something to SHOW, and nothing else.
 * 2. **Keep {placeholders} exactly as they are.** `{count}`, `{name}`,
 *    `{amount}` are filled in at runtime. Translating the word inside the
 *    braces leaves the brace text on screen.
 *
 * A phrase that changes with a number uses `{ one, other }` and is looked up
 * with `t('key', { count })`. Italian pluralises the way English does - one,
 * or not one - so the two files have the same shape.
 */

export default {
  /* ---------------------------------------------------------------
     COMMON - words that appear on many screens. Keep this small; a
     shared phrase that only fits two screens belongs on those screens,
     because a word that is right in both English contexts is often
     wrong in one of the Italian ones.
     --------------------------------------------------------------- */
  common: {
    loading: 'Loading...',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
    yes: 'Yes',
    no: 'No',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    confirm: 'Confirm',
    somethingWrong: 'Something went wrong. Please try again.',
    perHour: 'about {amount} per hour',
    loadingConversations: 'Loading conversations...',
    checkingLogin: 'Checking your login...',
    loadingChore: 'Loading chore...',
    checkingLink: 'Checking your link...',
    findingChores: 'Finding chores...',
    loadingMessages: 'Loading your messages...',
    loadingNotifications: 'Loading notifications...',
    backToMyChores: 'Back to my chores',
    welcomeBack: 'Welcome back, {name}.',
  },

  /* ---------------------------------------------------------------
     LANGUAGE SWITCH
     --------------------------------------------------------------- */
  language: {
    label: 'Language',
    change: 'Change language',
    // Named in their OWN language, always. Somebody looking for Italian is
    // looking for the word "Italiano", not for "Italian" written in a
    // language they are trying to get away from.
    it: 'Italiano',
    en: 'English',
  },

  /* ---------------------------------------------------------------
     NAVIGATION
     --------------------------------------------------------------- */
  nav: {
    browse: 'Browse chores',
    myOffer: 'My offer',
    myWork: 'My work',
    myChores: 'My chores',
    whosFree: 'Available students',
    students: 'Students',
    moderation: 'Moderation',
    messages: 'Messages',
    notifications: 'Notifications',
    logIn: 'Log in',
    signUp: 'Sign up',
    logOut: 'Log out',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    messagesUnread: 'Messages, {count} unread',
    notificationsUnread: 'Notifications, {count} unread',
    users: 'Users',
  },

  /* ---------------------------------------------------------------
     FRONT PAGE
     --------------------------------------------------------------- */
  home: {
    // The <em> half is a separate key so the emphasis survives translation -
    // where the stress falls in an Italian sentence is not where it falls here.
    headlineA: 'Jobs you keep putting off.',
    headlineB: "Students who'd rather be earning.",
    lede: "Post what needs doing and what you'll pay. Students from the school apply, you choose based on reviews.",
    ctaPoster: 'I need a job doing',
    ctaStudent: "I'm a student",
    ctaBrowse: 'Browse the board',
    costFree: "It's free, both sides.",
    costPaid: 'Five euros a year to post.',
    costBody: 'Students keep everything they earn, simply agree a price together and arrange payment between yourselves.',
    boardTitle: 'On the board right now',
    boardBody: 'Chores posted by people connected to school.',
    seeAll: 'See all {count}',
    closeTitle: 'Have a look before you sign up.',
    closeBrowse: "See what's on",
    closeSignUp: 'Create an account',
  },

  /* ---------------------------------------------------------------
     LOGGING IN
     --------------------------------------------------------------- */
  login: {
    title: 'Welcome back',
    subtitle: 'Log in to see your chores, applications and messages.',
    email: 'Email address',
    password: 'Password',
    forgot: 'Forgotten it?',
    submit: 'Log in',
    noAccount: 'Do not have an account?',
    signUp: 'Sign up',
    checkEmail: 'Check your email',
    yourCode: 'Your code',
  },

  /* ---------------------------------------------------------------
     CHOOSING AN ACCOUNT TYPE
     --------------------------------------------------------------- */
  signupChoice: {
    title: 'Which kind of account?',
    posterTitle: 'I need chores done',
    posterBody: 'Post jobs and pick a student to do them.',
    studentTitle: 'I want to earn',
    studentBody: 'Apply for jobs near you, and say when you are free.',
    // The same without "say when you are free", which is offers - shown while
    // they are archived (pages/SignupChoice.jsx).
    studentBodyNoOffers: 'Find jobs near you and apply for the ones that suit you.',
    haveAccount: 'Already have an account?',
    subtitle: 'Two kinds of account, depending on which side of the chore you are on.',
    studentCta: 'Sign up as a student',
    posterCta: 'Sign up to post chores',
  },

  /* ---------------------------------------------------------------
     THE MARKETPLACE, AND ONE CHORE ON A CARD
     --------------------------------------------------------------- */
  marketplace: {
    title: 'Chores',
    subtitle: 'Jobs posted by people near the school.',
    search: 'Search',
    searchPlaceholder: 'lawn, dog, shopping...',
    category: 'Category',
    allCategories: 'All categories',
    minPay: 'Lowest pay',
    maxPay: 'Highest pay',
    withinKm: 'Within (km)',
    sort: 'Sort by',
    sortNewest: 'Newest',
    sortSoonest: 'Starting soonest',
    sortPay: 'Best paid',
    sortDistance: 'Nearest',
    sortRelevance: 'Best match',
    moreFilters: 'More filters',
    fewerFilters: 'Fewer filters',
    filtersSet: '{count} set',
    empty: 'Nothing on the board yet.',
    emptyFiltered: 'No chores match what you are looking for.',
    resultCount: { one: '{count} chore', other: '{count} chores' },
  },

  chore: {
    noBackup: 'No backup',
    applyAsBackup: 'Apply as backup',
    aboutHours: 'about {hours}h',
    aboutHoursEach: 'about {hours}h each',
    visits: { one: '{count} visit', other: '{count} visits' },
    daysFrom: '{count} days from {date}',
    payEach: '{amount} × {count}',
    eachTime: '{amount} each time',
    totalInAll: '{amount} in all',
  },

  /* ---------------------------------------------------------------
     STATUSES - shown as badges all over the site
     --------------------------------------------------------------- */
  status: {
    open: 'Open for applications',
    assigned: 'Booked',
    completed: 'Done',
    cancelled: 'Cancelled',
    expired: 'Expired - nobody applied',
  },

  /* ---------------------------------------------------------------
     CHORE CATEGORIES - DISPLAY ONLY.
     The key is the value stored in the database and must never change.
     See the warning at the top of this file.
     --------------------------------------------------------------- */
  categories: {
    Cleaning: 'Cleaning',
    Gardening: 'Gardening',
    Babysitting: 'Babysitting',
    'Pet Care': 'Pet care',
    Tutoring: 'Tutoring',
    'Shopping & Errands': 'Shopping & errands',
    'Moving & Lifting': 'Moving & lifting',
    'Tech Help': 'Tech help',
    'DIY & Repairs': 'DIY & repairs',
    Other: 'Other',
  },



  /* ---------------------------------------------------------------
     GETTING BACK IN, AND CONFIRMING AN ADDRESS
     --------------------------------------------------------------- */
  forgot: {
    title: 'Forgotten your password?',
    subtitle: 'Tell us your email address and we will send you a link to set a new one.',
    email: 'Email address',
    submit: 'Send me a reset link',
    sending: 'Sending...',
    checkSpam: 'The link works for one hour. If nothing arrives, check your spam folder and make sure you typed the same address you signed up with.',
    backToLogin: 'Back to log in',
    backToLogIn: 'Back to log in',
  },

  confirmEmail: {
    failedTitle: 'We could not confirm that link',
    alreadyDone: 'If you have already confirmed, there is nothing more to do - just log in.',
    goToLogin: 'Go to log in',
    thanksNamed: 'Thank you, {name}',
    thanks: 'Thank you',
    done: 'Your email address is confirmed. You can now post chores, apply for them, and arrange work with people.',
    goToDashboard: 'Go to my dashboard',
  },

  /* ---------------------------------------------------------------
     SETTINGS
     --------------------------------------------------------------- */
  settings: {
    languageTitle: 'Language',
    languageBody: 'Changes the site straight away. It is also the language we write to you in - your emails follow this setting.',
    languageSaved: 'Saved. Emails will come in {language} from now on.',
  },

  /* ---------------------------------------------------------------
     FOOTER
     --------------------------------------------------------------- */

  /* ---------------------------------------------------------------
     THE MODERATOR'S SECOND STEP, AND SETTING A NEW PASSWORD
     --------------------------------------------------------------- */
  twoFactor: {
    sentTo: 'We have sent a six-digit code to {email}. It works for the next {minutes} minutes.',
    noCode: 'No code? Check your spam folder, or use one of your recovery codes.',
    checking: 'Checking...',
    startAgain: 'Start again',
    tooSlow: 'That took too long. Please enter your password again.',
    sendNewCode: 'Send a new code',
  },

  reset: {
    titleDone: 'Password changed',
    title: 'Choose a new password',
    done: 'Your password has been changed. Taking you to the login page...',
    newPassword: 'New password',
    rule: 'At least 8 characters, including a number.',
    again: 'Type it again',
    submit: 'Set new password',
    mismatch: 'The two passwords do not match.',
    tooWeak: 'Your password must be at least 8 characters and include a number.',
    logInNow: 'Log in now',
    requestNewLink: 'Request a new link',
  },


  /* ---------------------------------------------------------------
     SIGNING UP - the shared form pieces, then each form
     --------------------------------------------------------------- */
  form: {
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email address',
    password: 'Password',
    phone: 'Phone number',
    optional: 'Optional',
    passwordRule: 'At least 8 characters, including a number.',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
  },

  terms: {
    agree: 'I have read and agree to the',
    termsLink: 'terms of service',
    and: 'and the',
    privacyLink: 'privacy notice',
    keptWhileReading: 'What you have typed is kept while you read - the back arrow brings you straight back to it.',
  },

  school: {
    label: 'Your school',
    placeholder: 'The school you go to',
    joiningCode: 'Invite code',
    codePlaceholder: 'The code you were given',
    joiningCodeHint: 'Whoever invited you to ChoreConnect has this. It is what keeps the site to people who were actually invited. Capitals and spaces do not matter.',
  },

  location: {
    address: 'Street address',
    yourAddress: 'Your address',
    town: 'Town',
    townHint: 'Start typing and pick from the list - that is what makes distances work.',
    addressHint: 'Only shown to the student you accept and your backup - never on the public listing.',
    usePrecise: 'Use my precise location',
    updatePrecise: 'Update to where I am now',
    findingYou: 'Finding you...',
    noBrowser: 'Your browser cannot share a location.',
    refused: 'Location permission was refused. Type your town and street instead.',
    failed: 'Could not work out where you are. Type your town and street instead.',
    nothingHere: 'We could not place you. Type your town and street instead.',
    noStreet: 'We could not find the street. Type it in.',
    // The phone fell back on wifi or the network and is kilometres out.
    tooVague: 'Your phone only knows where you are to within about {km} km. Type your town and street instead.',
    addressNotFound: 'We could not find that street. Check the spelling, and the town.',
    findFailed: 'Could not look that up just now.',
    tooMany: 'Too many lookups for now. Wait a few minutes.',
    creditBefore: 'Street names:',
    creditLink: '© OpenStreetMap contributors',
  },

  userSignup: {
    title: 'Post chores on ChoreConnect',
    subtitlePaid: 'Create your account, then join for €5 a year to start posting.',
    subtitleFree: 'Create your account and post your first chore. It is free.',
    creating: 'Creating your account...',
    create: 'Create account',
    areYouStudent: 'Are you a student?',
    signUpHere: 'Sign up here instead',
    registered: 'Already registered?',
  },


  /* ---------------------------------------------------------------
     APPLICATION STATUSES, and the small shared pieces
     --------------------------------------------------------------- */
  applicationStatus: {
    pending: 'Awaiting decision',
    accepted: 'Accepted',
    backup: 'Backup',
    rejected: 'Not chosen',
    cancelled: 'Chore called off',
    withdrawn: 'Withdrawn',
  },

  review: {
    howWasIt: 'How did it go with {name}?',
    yourReview: 'Your review (optional)',
    placeholder: 'What went well? Would you work with them again?',
    submit: 'Submit review',
    chooseStars: 'Please choose a star rating.',
    noneYet: 'No reviews yet',
  },

  /* Date of birth, as three lists - components/BirthDateFields.jsx. */
  birthDate: {
    day: 'Day',
    dayShort: 'Day',
    month: 'Month',
    year: 'Year',
    impossible: 'That date does not exist - check the day and the month.',
  },

  studentSignup: {
    title: 'Start earning with ChoreConnect',
    subtitle: 'Free to join. Browse chores near you and apply for whatever suits.',
    age: 'Age',
    ageRule: 'You must be 16 or over to work through ChoreConnect.',
    dateOfBirth: 'Date of birth',
    dateOfBirthHint: 'You must be 16 or over. Other people only ever see your age, never this date.',
    dateOfBirthAge: 'That makes you {age}. Other people only ever see your age, never this date.',
    schoolYear: 'School year',
    about: 'About you',
    aboutPlaceholder: 'A couple of sentences about what you are good at and when you are free. This is the first thing people read when deciding who to pick.',
    skills: 'What are you happy to do?',
    skillsHint: 'Pick as many as you like - these show on your profile.',
    guardianTitle: 'Because you are under 18, we need a parent or guardian to agree.',
    guardianBody: 'We will email them directly - they have to confirm, not you. They will be asked about five things: applying for jobs, jobs that repeat over several days, publishing when you are free, messaging the person whose job you take, and alerts on your phone. You can look around straight away, but you cannot apply for work until they answer.',
    guardianName: "Parent or guardian's name",
    guardianEmail: 'Their email address',
    guardianEmailHint: 'Must be theirs, not yours.',
    needChores: 'Need chores done instead?',
    signUpHere: 'Sign up here',
  },

  marketplaceExtra: {
    heading: 'Chores near you',
    subtitleStudent: 'Find work that fits around school. Apply for anything that suits and the poster will get back to you.',
    subtitleOther: 'Everything currently open for applications across the community.',
    searching: 'Searching...',
    found: { one: '{count} chore found', other: '{count} chores found' },
    emptyTitle: 'No chores match that',
    emptyFiltered: 'Try widening your filters - a bigger distance or a lower minimum pay.',
    emptyNone: 'Nothing is open right now. Check back soon.',
    looseSearch: 'Nothing matched every word you typed, so these are the closest matches.',
  },


  /* ---------------------------------------------------------------
     THE PAGE A PARENT OPENS

     This is the other half of the consent email, and the two have to say
     the same thing. The email's copy lives in `backend/i18n/`, because the
     server writes it; this one is drawn by the browser. Change either and
     change both, and bump CONSENT_VERSION - see HANDOFF section 7 item 1.

     Written in the FORMAL register in Italian ("Lei"), same as the email:
     it is a request to a parent we have never met about their child.
     --------------------------------------------------------------- */
  consent: {
    linkInvalid: 'This link is not valid.',
    titleNew: 'A request for your permission',
    titleRenewal: 'Please have another look',
    introNew: '{fullName} has signed up to ChoreConnect. Because they are under 18, nothing can happen until you agree.',
    introRenewal: 'ChoreConnect does more than it did when you agreed to {name} using it, so we are asking you again rather than assuming.',
    changedTitle: 'What has changed since you last agreed.',
    changedBody: '{name} can now be booked for jobs that run over several days, publish when they are free for adults to see, message the person whose job they have taken, and receive alerts on their phone. All of it is set out below.',
    agreedOn: 'You agreed to the earlier version on {date}.',
    untilYouAnswer: 'Until you answer, {name} cannot apply for work or be booked by anybody.',
    already: 'You have already confirmed this. There is nothing more to do.',
    whatIsIt: 'What ChoreConnect is',
    whatIsItBody: 'A local noticeboard where people post small jobs - gardening, cleaning, babysitting, tutoring, help with shopping - and school students apply to do them for an agreed fee.',
    introOnlyTitle: 'We are an introduction service only.',
    introOnlyBody: 'We do not employ anybody, we do not handle the money, and we do not supervise the work. Payment is arranged directly between your child and the person who posted the job.',
    whatYouAgree: 'What you are agreeing to',
    whatWeHold: 'And what we hold',
    fullDetail: 'Full detail is in our',
    privacyNotice: 'privacy notice',
    andThe: 'and',
    termsOfService: 'terms of service',
    readFirst: '. Please read them before deciding.',
    schoolYear: 'School year',
    school: 'School',
    yourDecision: 'Your decision',
    agreeNew: 'Yes, I agree to {name} using ChoreConnect',
    agreeRenewal: 'Yes, I still agree to {name} using ChoreConnect',
    declineNew: 'No, I do not agree',
    declineRenewal: 'No, I would rather they stopped',

    /* The five things, as the PAGE shows them - a title and a body each.
       The email says the same five in plain prose. Keep them in step. */
    agree1Title: 'Applying for jobs',
    agree2Title: 'Jobs that repeat over several days',
    agree3Title: 'Saying when they are free',
    agree4Title: 'Messages about an agreed job',
    agree5Title: 'Alerts on their phone',

    agree1Body: '{name} can apply for jobs posted on the site, and travel to the address of somebody who accepts them. They choose what to apply for - nobody is given work.',
    agree2Body: 'A job can run over more than one day - walking a dog every morning for a week, or babysitting every Wednesday and Saturday for up to three months. So {name} can agree once to visiting the same person\u2019s home many times over, which is worth knowing about specifically. They see every date before accepting, nothing repeats indefinitely, and they can drop any single day up to 24 hours beforehand - or stop the whole arrangement - without it counting against them.',
    agree3Body: '{name} can publish the times they are available, and the adults on the site can see that and ask about a particular time. Not other students, and nobody who is signed out. Nothing is booked unless {name} says yes, and an address is only shared once they have.',
    agree4Body: 'Once a job is agreed, {name} and that one person can message each other about it here - which door, what to bring, running late. Only that person, only that job, and it closes a week afterwards. Messages are private, but they are kept, and a moderator can read a conversation in two situations: if anybody reports it, or if the site notices contact details being shared - a phone number, an email address, a request to carry on somewhere else. That second one exists because a child being talked into moving off the site does not usually report it. Nobody reads conversations otherwise, and every time a moderator opens one it is recorded.',
    agree5Body: 'If {name} turns them on, the site can send alerts about new jobs to their phone. Off unless they choose it, off again whenever they like, and nothing between 9pm and 7:30am.',

    hold0: '{name}’s name, date of birth, school, school year, town, and anything they write about themselves. Other people on the site see their age, never the date.',
    hold1: 'Reviews they leave and receive, shown publicly under their first name and last initial.',
    hold2: 'The messages described above, kept as a record rather than deleted.',
    hold3: 'Your name and email address, solely as the record of this permission.',
    askChildResend: 'Ask your child to log in and choose "resend" from their dashboard, and a fresh link will arrive.',
    thankYouConfirmed: 'Thank you. {name} can now use ChoreConnect.',
    thankYouDeclined: 'Thank you. We have recorded that you do not consent, and {name} cannot apply for work or be booked by anybody.',
    copyEmailed: 'We have emailed you a copy for your records, listing what you agreed to. You can withdraw it at any time by replying to that email, and we will close the account. If we ever change what the site does, we will ask you again rather than assume.',
    findOutMore: 'Find out more about ChoreConnect',
    whatTheyToldUs: 'What {name} told us',
    name: 'Name',
    age: 'Age',
    ifWrong: 'If any of this is wrong, or you were not expecting this email, please decline below.',
  },


  /* ---------------------------------------------------------------
     THE BELL, THE MESSAGE LIST, THE STUDENT LIST, AND 404
     --------------------------------------------------------------- */
  notifications: {
    title: 'Notifications',
    unread: '{count} unread',
    caughtUp: 'You are all caught up.',
    markAll: 'Mark all as read',
    emptyTitle: 'Nothing yet',
    emptyBody: 'When someone applies for your chore, accepts you for a job, or leaves a review, it will show up here.',
    remove: 'Remove',
  },

  messages: {
    seeArchived: 'Archived',
    backToInbox: 'Back to your conversations',
    putBack: 'Put back',
    aQuestion: 'a question',
    aboutTheirTimes: 'About their availability',
    title: 'Messages',
    subtitle: 'One conversation for each job. A student can ask about a job that is open for applications, and the two people on an agreed job can talk about it here.',
    emptyTitle: 'No conversations yet',
    emptyBody: 'Once a chore is agreed, you and the other person can message each other here to sort out the details.',
    you: 'You:',
  },

  directory: {
    title: 'Students',
    subtitle: 'Everyone available for work, with the highest rated first. Click through to read their reviews before you decide.',
    schoolYear: 'School year',
    anyYear: 'Any year',
    minRating: 'Minimum rating',
    anyRating: 'Any rating',
    searching: 'Searching...',
    count: { one: '{count} student', other: '{count} students' },
    emptyTitle: 'No students match that',
    emptyBody: 'Try clearing the filters or searching for something broader.',
    searchPlaceholder: 'Name, or something they are good at',
    fourUp: '4 stars and up',
    fourHalfUp: '4.5 stars and up',
    fiveOnly: '5 stars only',
    usersTitle: 'People who post chores',
    usersSubtitle: 'What adults on the site are showing to students. A photograph, a name and a town are all somebody sees before deciding whether to go to that person’s house.',
    searchPeople: 'Search',
    usersSearchPlaceholder: 'Name or email address',
    includeSuspended: 'Include people who are suspended',
    loadingUsers: 'Loading people...',
    noUsers: 'Nobody to show',
    noUsersBody: 'No account matches that. Try a shorter search, or include suspended people.',
    usersFound: { one: '{count} person', other: '{count} people' },
    backToModeration: 'Back to moderation',
    studentsIncludeSuspended: 'Include students who are suspended',
  },

  notFound: {
    title: 'Page not found',
    body: 'That page does not exist. It may have been moved, or the chore might have been taken down.',
    home: 'Back to the home page',
  },


  /* ---------------------------------------------------------------
     THE TWO DASHBOARDS, AND "WHO'S FREE"
     --------------------------------------------------------------- */
  dashboard: {
    cancelStillFine: 'You can still cancel - it starts {when}.',
    cancelLateNote: 'It starts {when}. You can still cancel, but you will have to say why.',
    hello: 'Hello, {name}',
    posterSubtitle: 'Everything you have posted, and anything waiting on you.',
    studentSubtitle: 'Your jobs and applications so far.',
    loadingPoster: 'Loading your dashboard...',
    loadingStudent: 'Loading your work...',
    postChore: 'Post a chore',
    membershipInactive: 'Your membership is not active.',
    membershipBody: 'You can browse and manage what you have, but you cannot post anything new.',
    setUpMembership: 'Set up membership (€5/year)',
    applicationsWaiting: 'Applications waiting',
    choresCompleted: 'Chores completed',
    waitingToHear: 'Waiting to hear back',
    yourRating: 'Your rating',
    comingUp: 'Coming up',
    whoApplied: 'Who has applied',
    waitingOnYou: { one: '{count} student is waiting for an answer', other: '{count} students are waiting for an answer' },
    seeWhoApplied: 'See who applied',
    yourChores: 'Your chores',
    reportSent: 'Sent. A moderator reads every report at 20:00.',
    workYouHaveDone: 'Work you have done',
    yourApplications: 'Your applications',
    reviewsToLeave: 'Reviews to leave',
    youAreBackup: 'You are backup',
    yours: 'Yours',
    awaitingDecision: 'Waiting on a decision.',
    nothingYet: 'Nothing here yet',
    applicationWithdrawn: 'Application withdrawn.',
    findMoreWork: 'Find more work',
    leaveAReview: 'Leave a review',
    jobsComingUp: 'Jobs coming up',
    startsSoon: 'Starts within 24 hours',
    cannotCancel: 'Cannot cancel',
    cancelThisJob: 'Cancel this job',
    doNotFeelSafe: 'I do not feel safe',
    messageName: 'Message {name}',
    standingBy: 'You are standing by. If the main student drops out, this becomes yours.',
    stepDown: 'Step down',
    withdraw: 'Withdraw',
    stepDownQ: 'Step down as backup?',
    cancelJobQ: 'Cancel this job?',
    keepIt: 'Keep it',
    cancellingRecorded: 'Cancelling is recorded on your profile. Try not to make a habit of it - reliability is what gets you the next job.',
    noBackup: 'No backup',
    postAChore: 'Post a chore',
  },

  offers: {
    title: 'Available students',
    subtitle: 'Students who have said when they are available. Ask one of them about a time that suits - nothing is booked until they say yes.',
    whatDoYouNeed: 'What do you need?',
    anything: 'Anything',
    loading: 'Finding students who are free...',
    emptyTitle: 'Nobody is free right now',
    emptyBody: 'No student has published availability matching that. You could post a chore instead and let students apply.',
    town: 'Town',
    seeTheirTimes: 'See their times',
    nextFree: 'Next free',
    timesAvailable: '{count} times available',
    countFree: { one: '{count} student is free', other: '{count} students are free' },
    // "Works in Ispra" is where they are WILLING to work. The distance badge
    // beside it is how far they are from you. Said separately because
    // "Ispra (2.9 km away)" read as though Ispra were 2.9km away.
    worksIn: 'Works in {city}',
    jobsDone: { one: '{count} job done', other: '{count} jobs done' },
  },

  /* ---------------------------------------------------------------
     "AVAILABLE STUDENTS" SINCE 17 OCTOBER 2026 - the counts that took the
     place of the offers above. pages/AvailableStudents.jsx.
     `readyFor` is keyed by the stored category, like `categories`: the keys
     must never change, only the words.
     --------------------------------------------------------------- */
  availableStudents: {
    title: 'Available students',
    subtitle: 'How many students can take on a job right now, and what they are happy to do.',
    loading: 'Counting students...',
    readyTotal: 'Students ready to complete chores',
    byKind: 'What they are happy to do',
    readyFor: {
      Cleaning: 'Students ready to clean',
      Gardening: 'Students ready to do gardening',
      Babysitting: 'Students ready to babysit',
      'Pet Care': 'Students ready to look after pets',
      Tutoring: 'Students ready to tutor',
      'Shopping & Errands': 'Students ready to run errands',
      'Moving & Lifting': 'Students ready to help with moving and lifting',
      'Tech Help': 'Students ready to help with tech',
      'DIY & Repairs': 'Students ready to do DIY and repairs',
      Other: 'Students ready for other jobs',
    },
    choresCompleted: 'Chores completed on ChoreConnect so far',
    onlyModerators: 'Only moderators can see this number for now.',
    ctaBody: 'Post a chore and the students who are ready can apply for it.',
  },


  /* ---------------------------------------------------------------
     ONE CHORE, IN FULL
     --------------------------------------------------------------- */
  choreDetail: {
    startsSoonTitle: 'This job starts soon.',
    startsSoonBody: 'It starts {when}. You can still cancel, but you will need to tell {name} why - they will see exactly what you write.',
    whyCancelling: 'Why can you not make it?',
    whyCancellingHint: 'This job starts soon, so {name} needs to know. A sentence is enough - they will see exactly what you write.',
    whyCancellingPlaceholder: 'I am ill and cannot get there. Sorry for the short notice.',
    whyCancellingShort: 'Please write a little more - this is what they will read.',
    loading: 'Loading chore...',
    backToMarketplace: 'Back to the marketplace',
    notFound: 'That chore could not be found.',
    whatNeedsDoing: 'What needs doing',
    address: 'Address:',
    whoIsDoing: 'Who is doing this',
    doingThis: 'Doing this chore',
    reviews: 'Reviews',
    yourTurn: 'Your turn to review',
    noReviews: 'No reviews left yet.',
    payment: 'Payment',
    yourApplication: 'Your application',
    noLongerTaking: 'This chore is no longer taking applications.',
    postedBy: 'Posted by',
    addMessage: 'Add a message (optional)',
    apply: 'Apply for this chore',
    applyBackup: 'Apply as backup',
    sending: 'Sending...',
    sendApplication: 'Send application',
    applicationSent: 'Application sent. You will hear back through your notifications.',
    applicationWithdrawn: 'Application withdrawn.',
    reviewSaved: 'Thanks - your review has been saved.',
    datesMovedTitle: 'The poster changed when this job happens',
    datesMovedBody:
      'You agreed to different dates, so you can cancel this job right up to the ' +
      'start if the new ones do not work for you. It will not count against you, ' +
      'and nobody will think worse of you for it.',
    stepDown: 'Step down as backup',
    cancelJob: 'Cancel this job',
    stepDownQ: 'Step down as backup?',
    cancelJobQ: 'Cancel this job?',
    cancelling: 'Cancelling...',
    yesCancel: 'Yes, cancel',
    removedBackup: 'You will be removed as the backup for this chore.',
    removedJob: 'You will be removed from this chore and the poster will be told.',
    removedNotice: 'You have been removed from this chore. It has gone back on the marketplace.',
    youAreBackup: 'You are the backup. If the main student drops out, this job becomes yours automatically.',
    jobIsYours: 'This job is yours. Turn up on time and check the address above.',
    jobIsYoursNoAddress: 'This job is yours. There is no street address on it yet - message {name} to ask where to go.',
    askAboutJob: 'Ask about this job',
    messageName: 'Message {name}',
    noAddressOwner: 'There is no street address on this chore, so whoever does it will not know where to go.',
    addAddress: 'Add the address',
    noAddressStudent: '{name} has not added a street address yet. Send them a message to ask where to go.',

    theDates: 'The dates',
    eachTimeTotal: '{each} each time, {total} in all.',
    cancelThisDay: 'Cancel this day',
    cannotMakeDay: 'I cannot make this day',
    dayCancelled: 'That day has been cancelled. The rest of the booking is unchanged.',
    cancelDayQ: 'Cancel just this day?',
    yesCancelDay: 'Yes, cancel this day',
    whyOptional: 'Why? (optional)',
    whyPosterPlaceholder: 'We are away that day',
    whyStudentPlaceholder: 'I have an exam that afternoon',
    whyToStudent: 'Passed on to the student, in your own words.',
    whyToPoster: 'Passed on to the poster. A short reason makes it much easier for them to plan.',
    noBackupChosen: 'No backup chosen',
    visitCancelled: ' - cancelled',
    visitDone: ' - done',
    dropSingleDay: 'You can drop any single day up to 24 hours before it. If something is wrong on the day itself, or you do not feel safe going, use the "I do not feel safe" button instead - that always works and never counts against you.',
    sharedBecauseInvolved: 'Shared with you because you are involved in this chore.',
    backupBadge: 'Backup',
    takingBackups: 'Somebody is already booked for this. Being the backup means you step in automatically if they drop out more than 24 hours beforehand - and you are told either way.',
    withdrawApplication: 'Withdraw application',
    onlyStudentsApply: 'Only student accounts can apply for chores.',
    signUpToApply: 'Sign up to apply',
    alreadyHaveAccount: 'Already have an account? Log in',
    applyPlaceholder: 'Tell them why you would be good for this - any experience, and when you are free.',
    keepIt: 'Keep it',
    cancellingShows: 'Cancelling shows on your record. If there is a backup they will take over automatically; if not, the chore goes back on the marketplace.',
  },


  /* ---------------------------------------------------------------
     POSTING A CHORE, INCLUDING THE MULTI-DAY CALENDAR
     --------------------------------------------------------------- */
  reportIssue: {
    title: 'Report an issue',
    subtitle:
      'Something on the site not working? Tell us here. This goes straight to whoever is looking after ChoreConnect.',
    listHint: 'Something broken? Tell us about it.',
    notForPeople:
      'This is for the website itself - a button that does nothing, a page that will not load. If somebody has made you uncomfortable, use the report button on their profile instead, or leave a job you do not feel safe at. Those go to the same people, faster.',
    empty: 'Nothing here yet. Describe what went wrong and what you were trying to do, and we will look into it.',
    placeholder: 'What were you doing, and what happened?',
    moderator: 'Moderator',
    send: 'Send',
    yourMessage: 'Your message',
  },

  emailOffline: {
    banner:
      'We cannot send emails at the moment, so signing up, confirming an address and resetting a password are unavailable. This is temporary and usually clears within a day.',
  },

  resend: {
    sentTitle: 'Email sent',
    sentBody: 'It is on its way to {email}. It usually arrives within a minute - if it does not, check your spam folder before asking for another.',
    waitFor: 'You can send another in {time}',
  },

  createChore: {
    flexibleLabel: 'I am flexible about the time',
    flexibleHint: 'Tick this if the day matters but the hour does not. Students will see "at a time that suits you both", and you can agree the hour between you once somebody has the job.',
    titleNew: 'Post a chore',
    titleEdit: 'Edit chore',
    lockedTitle: 'A student has agreed to this job',
    lockedBody:
      'You can still change the day and the time, and they will be told. ' +
      'The rest is fixed now - they said yes to the job as it stands. ' +
      'If the work itself has changed, cancel this one and post the new job.',
    addMissingAddress:
      'One more thing can still be added: the street address. This chore has none, ' +
      'and the student needs it to find you - the box is at the bottom of the form.',
    lockedDatesWarning:
      'Changing the day or time lets this student cancel with no notice, ' +
      'and it will not count against them. Tell them why if you can.',
    lede: 'The clearer you are about what is involved, the better the applications you will get.',
    needMembership: 'You need an active membership to post chores.',
    membershipBody: 'ChoreConnect is €5 a year for people posting chores, and always free for students.',
    seeMembership: 'See membership options',
    fieldTitle: 'Title',
    titlePlaceholder: 'Mow the back lawn and trim the hedge',
    whatNeedsDoing: 'What needs doing?',
    descriptionPlaceholder: 'Describe the job, roughly how long it takes, and anything the student should bring or know. Mention if equipment is provided.',
    descriptionHint: '{count}/2000 - at least 20 characters',
    category: 'Category',
    payment: 'Payment (€)',
    paymentPerVisit: 'Payment per visit (€)',
    paySum: '{each} × {count} = {total}',
    whereIsIt: 'Where is it?',
    postIt: 'Post this chore',
    saveChanges: 'Save changes',

    whenTitle: 'When is it needed?',
    whenLegend: 'How often is this needed?',
    modeOne: 'One day',
    modeOneHint: 'A single job at a single time',
    modeDates: 'Several days',
    modeDatesHint: 'Pick the days on a calendar',
    modeWeekly: 'Every week',
    modeWeeklyHint: 'The same days each week, until a date you choose',
    whatTime: 'What time?',
    whatTimeEach: 'What time each day?',
    howLong: 'Roughly how long? (hours)',
    howLongEach: 'How long each time? (hours)',
    whichDay: 'Which day?',
    whichDays: 'Which days?',
    whichDaysHint: 'Tap every day you need somebody. They do not have to be in a row - skip the days you have covered.',
    whichWeekdays: 'Which days of the week?',
    until: 'Keep going until',
    untilHint: 'Tap the last day it should run. Up to {months} months ahead - you can always cancel or change it later.',
    runsUntil: 'Runs until',
    visitCount: { one: '{count} visit', other: '{count} visits' },
    eachAndTotal: '{each} each · {total} in all',
    first: 'First: {date}',
    last: 'Last: {date}',
    seeEveryDate: 'See every date',
    overLimit: 'That is more than {max} visits, which is the most one booking can hold. Choose a nearer finishing date, or fewer days a week.',
    pickWeekly: 'Pick at least one day of the week and a date to run until.',
    pickDays: 'Pick at least one day on the calendar.',
    mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
    fiveAYear: 'ChoreConnect is €5 a year for people posting chores, and always free for students.',
    subtitle: 'The clearer you are about what is involved, the better the applications you will get.',
  },


  /* ---------------------------------------------------------------
     PROFILES, THE CONVERSATION, AND THE TWO BANNERS
     --------------------------------------------------------------- */
  profile: {
    loading: 'Loading profile...',
    posterNotFound: 'That person could not be found.',
    studentNotFound: 'That student could not be found.',
    atAGlance: 'At a glance',
    choresCompletedByStudents: 'Chores completed by students',
    openRightNow: 'Open right now',
    reviewsFromStudents: 'Reviews from students',
    choresOpenNow: 'Chores open now',
    trackRecord: 'Track record',
    reviewsReceived: 'Reviews received',
    happyToDo: 'Happy to do',
    recentWork: 'Recent work',
    noReviews: 'No reviews yet.',
    editMyProfile: 'Edit my profile',
    choresCompleted: 'Chores completed',
    joined: 'Joined {date}',
    reviewsWithCount: 'Reviews ({count})',
    noReviewsBody: 'No reviews yet. Everyone starts somewhere - the first job is the hard one.',
  },

  thread: {
    loading: 'Loading the conversation...',
    none: 'There is no conversation here.',
    backToMessages: 'Back to messages',
    yourMessage: 'Your message',
    send: 'Send',
    archive: 'File this away',
    theyAreAsking: '{name} is asking about this job. They have not applied for it and nothing is booked.',
    askingAbout: 'You are asking about this job. Nothing is applied for and nothing is booked - the address is only shared if you take the work.',
    closedTaken: 'This job has been booked, so the conversation is now read-only.',

    /* ---- A question about a student's offer. The other direction: here it
       is the adult who wrote first, so the wording says whose choice the
       answer is. See backend/routes/messages.js. ---- */
    aboutTheirTimes: 'About their availability',
    theyAskedAboutYourTimes: '{name} has asked about the times you have put up. It is up to you whether you answer - they cannot write again unless you do.',
    youAskedAboutTheirTimes: 'You have asked about this student’s availability. Nothing is booked, and they decide whether to answer.',
    waitingForAnswer: 'You have asked your question. You can write again once {name} has answered - that is their choice to make.',
    closedOfferGone: 'This offer is no longer up, so the conversation is read-only.',
    // Offers were archived on 17 October 2026. "And report it" is said out
    // loud on purpose: the reason these stay readable is so that a student
    // can still report what an adult wrote.
    closedOffersOff: 'Offers are no longer on ChoreConnect, so this conversation is read-only. You can still read it, and report it.',
    keptNotice: 'Messages here are kept, and can be read by a moderator if anybody reports them.',
    closedCancelled: 'This chore was cancelled, so the conversation is closed. You can still read it.',
    closedFinished: 'This job finished a while ago, so the conversation is now read-only. You can still read it.',
    noMessagesYet: 'No messages yet. Say hello, or ask anything you need to know about the job.',
    phoneWarning: 'That looks like a phone number or email. You can still send it - but keeping the conversation here means there is a record if anything ever goes wrong.',
    messagePlaceholder: 'Message...',
  },

  confirmBanner: {
    title: 'Please confirm your email address',
    body: 'We sent a link to {email} when you signed up. You can look around until you open it, but you cannot post a chore, apply for one, or arrange anything with anybody.',
    resend: 'Send the email again',
    sending: 'Sending...',
    spamHint: 'Check your spam folder - that is usually where it has gone.',
  },

  guardianBanner: {
    titleWaiting: 'Waiting for a parent or guardian',
    titleRenewal: 'We need to ask your parent again',
    theirName: 'Their name',
    theirEmail: 'Their email address',
    resend: 'Send the email again',
    yourGuardian: 'your parent or guardian',
    renewalBefore: 'ChoreConnect has changed since',
    renewalAfter: 'agreed - you can now say when you are free, message the person whose job you take, and get alerts on your phone. We have asked them to look again. You can browse in the meantime, but you cannot apply for work or be booked until they answer.',
    waitingBefore: 'You can browse chores and set up your profile, but you cannot apply for work until',
    waitingAfter: 'confirms. We emailed them a link when you signed up.',
    saveAndSend: 'Save and send',
    wrongAddress: 'Wrong address?',
    spamHint: 'Ask them to check their spam folder - that is usually where it has gone.',
  },


  /* ---------------------------------------------------------------
     ASKING A STUDENT ABOUT THEIR TIMES, AND MANAGING YOUR OWN CHORE
     --------------------------------------------------------------- */
  offerDetail: {
    howLong: 'For how many hours?',
    worksInUpTo: 'Works in {city}, and travels up to {km}km',
    worksInAnywhere: 'Works in {city}, and is happy to travel',
    pickDaysHint: 'Choose the days you need {name}, then the time and how long for. Several days go as one request they answer once.',
    tooEarly: 'Please pick a time from 07:00 onwards. Students cannot be asked to work through the night.',
    tooMany: 'That is more than {max} days in one request. Send the rest separately.',
    usuallyFree: 'When {name} says they are usually free',
    whatTime: 'What time?',
    whenDoYouNeedThem: 'When do you need them?',
    whichDays: 'Which days?',
    loading: 'Loading...',
    notFound: 'That offer could not be found.',
    back: 'Back to available students',
    pickHint: 'Pick one time, or several. Several go as a single request that {name} answers once - so you will not end up with two days out of the three you needed.',
    alreadyAsked: 'Asked - waiting for a reply',
    chosen: 'Chosen',
    chooseThis: 'Choose this time',
    timesChosen: { one: '{count} time chosen', other: '{count} times chosen' },
    askAboutOne: 'Ask about this time',
    askAboutMany: 'Ask about these {count} times',

    /* ---- Asking something before proposing a time ---- */
    questionTitle: 'Something you need to know first?',
    questionBody: 'You can ask {name} one question before you propose a time - whether you mind dogs, how far they will walk, anything that decides it.',
    questionWaiting: 'You have asked your question. {name} decides whether to answer.',
    askAboutOffer: 'Ask about this offer',
    seeConversation: 'See the conversation',
    sendQuestion: 'Send the question',
    sendingQuestion: 'Sending...',
    questionRules: 'One question. {name} chooses whether to answer, and you cannot write again unless they do. Nothing here books anything.',
    yourQuestion: 'Your question',
    questionPlaceholder: 'Keep it short and about the work - "do you mind a large dog?", "could you manage 6pm rather than 5?"',
    askTitleOne: 'Ask {name} about {when}',
    askTitleMany: 'Ask {name} about {count} times',
    sending: 'Sending...',
    sendRequest: 'Send the request',
    whatIsJob: 'What is the job?',
    jobPlaceholder: 'Babysitting for the evening',
    payOne: 'What will you pay? (€)',
    payEach: 'What will you pay each time? (€)',
    paySum: '{each} × {count} times = {total} in all',
    payHint: '{name} sees this before deciding. Cash goes directly between the two of you - ChoreConnect never handles it.',
    address: 'Address',
    addressPlaceholder: 'Via Piave 14, second floor',
    addressHint: 'Only shared if they accept. Until then they see the town only.',
    anythingElse: 'Anything they should know?',
    anythingPlaceholder: 'Two children, 6 and 9. Both will have eaten, bedtime is 8pm.',
  },

  manage: {
    loading: 'Loading applications...',
    ageYears: 'Age {age}',
    notFound: 'That chore could not be found.',
    back: 'Back to my chores',
    markComplete: 'Mark complete',
    completed: 'Marked complete.',
    reviewHeading: 'Leave a review',
    reviewNotNow: 'Not now',
    reviewSaved: 'Thank you. Your review is saved.',
    cancelled: 'Chore cancelled. Everyone involved has been told.',
    considerBackup: 'Consider naming a backup.',
    noApplications: 'No applications yet',
    noApplicationsBody: 'Students in the area will see this chore on the marketplace. Give it a day or two - and check the pay is realistic for the work involved.',
    removeBackup: 'Remove as backup',
    turnDown: 'Turn down',
    completeQ: 'Mark this chore complete?',
    cancelQ: 'Cancel this chore?',
    cancelReason: 'Reason (optional, shared with them)',
    cancelPlaceholder: 'Plans changed, no longer needed...',
    deleteQ: 'Delete this chore?',
    deleteBody: 'This removes the chore entirely. It cannot be undone.',
    noBackupChosen: 'No backup chosen',
    viewPublicPage: 'View public page',
    deleteChore: 'Delete',
    cancelChore: 'Cancel chore',
    considerBackupBody: 'If your main student has to drop out, a backup steps in automatically instead of the job falling through.',
    accept: 'Accept',
    makeBackup: 'Make backup',
    notYet: 'Not yet',
    yesItIsDone: 'Yes, it is done',
    keepIt: 'Keep it',
    cancelTheChore: 'Cancel the chore',
    cancelBody: 'Everyone who applied will be told, and any student booked on it will be released.',
    deletePermanently: 'Delete permanently',
  },


  /* ---------------------------------------------------------------
     WITHDRAWING CONSENT, BEING SUSPENDED, AND WALKING AWAY
     --------------------------------------------------------------- */
  withdraw: {
    invalid: 'This link is not valid.',
    title: 'Withdraw your permission',
    forWhom: 'For {name}',
    whatHappens: 'What happens if you do',
    effect1: 'They can no longer apply for work.',
    effect2: 'They can no longer publish when they are free, and nobody can book them.',
    effect3: 'They can still sign in and look around, and any work already agreed still stands.',
    effect4: 'Their account is not deleted, and neither are their reviews.',
    doneTitle: 'Done, and it has taken effect already.',
    doneBody: '{name} can no longer apply for work, publish when they are free, or be booked by anybody.',
    doneCopy: 'We have emailed you a copy for your records. Their account still exists and any work already agreed still stands.',
    nothingTitle: 'There is nothing to withdraw.',
    nothingBody: '{name} does not currently have your permission, so they already cannot apply for work or be booked.',
    deleteInstead: 'If you would rather the account was removed altogether',
    deleteInsteadBody: ', reply to any email from us and say so. That is a different thing and we will not do it by accident.',
    replyToWithdraw: 'If you were trying to withdraw your permission, reply to any email we have sent you and a person will do it for you.',
    deleteAltogether: 'Their account still exists and any work already agreed still stands - if you would rather it was removed altogether, reply to that email and we will do it.',
    canAskAgain: '{name} can ask you again from their own account, which sends a fresh request to this address. Nothing happens unless you agree to it, and ignoring it leaves things exactly as they are now.',
    aboutChoreConnect: 'About ChoreConnect',
    nothingWhy: 'That will be because you withdrew it before, or because we changed what we ask parents to agree to and are waiting on a fresh answer. Either way, nothing is happening without you.',
    agreedOn: ', agreed on {date}',
    saving: 'Saving...',
    yesWithdraw: 'Yes, withdraw my permission for {name}',
    noLeave: 'No, leave things as they are',
  },

  ban: {
    closedTitle: 'Your account has been closed',
    suspendedTitle: 'Your account is suspended',
    permanentBody: 'This is not temporary. If you think it is a mistake, reply to any email we have sent you, or write to',
    mistakeBody: 'If you think this is a mistake, write to',
    over: 'Your suspension has ended. You can log in now.',
    days: { one: '{count} day', other: '{count} days' },
    hours: { one: '{count} hour', other: '{count} hours' },
    minutes: { one: '{count} minute', other: '{count} minutes' },
    and: ' and ',
    listJoin: ', ',
    lessThanMinute: 'less than a minute',
    timeLeft: '{time} left',
    canLogInAfter: 'You can log in again after that - nothing else is needed from you.',
    backHome: 'Back to the home page',
  },

  leave: {
    title: 'Leave this job',
    leaving: 'Leaving...',
    // The same screen, for a job that is already over - see
    // components/LeaveUnsafeDialog.jsx.
    reportTitle: 'Tell a moderator',
    sending: 'Sending...',
    aboutThisJob: 'This is about',
    overNowBody: 'The job is over, so there is nothing to leave - but if something happened while you were there, somebody should know about it.',
    notToldDoneBody: 'Nothing about the job changes either - it stays exactly as it is.',
    emergency: 'If you are in danger right now, call 112.',
    emergencyBody: 'Do that first. This can wait.',
    noObligation: 'You do not have to finish the job, and you do not have to stay.',
    noObligationBody: 'You are out of it the moment you press the button - nothing waits on anybody reading what you write.',
    noCount: 'It will not count against you.',
    noCountBody: 'It is not a cancellation and it will not show on your profile.',
    notTold: 'They will not be told you reported anything.',
    notToldBody: 'They only find out the job is free again.',
    // Was "somebody will look at this today" until 25 September 2026. With one
    // part-time moderator that was not a promise the site could keep at two in
    // the morning, and a safety promise you cannot keep is worse than a modest
    // one you can. The hour is the moderator's, agreed 25 September 2026.
    readAtEight: 'A moderator reads this at 20:00.',
    readAtEightBody: 'Reports are read once a day, at that time - nobody is watching the site overnight.',
    whatHappened: 'What happened?',
    whatHappenedHint: 'A few sentences. Where you were, what was said or done, and what made you want to leave. A moderator reads this at 20:00 and it is all they have to go on.',
    onlyModerator: 'Only a moderator reads this. It is not shown to the person who posted the job.',
    misuseTitle: 'This is for feeling unsafe, not for changing your mind.',
    misuseBody: 'Using it to get out of a job you simply do not want is a breach of the terms of service, and can lead to your account being suspended. If you just cannot make it, cancel the job instead and say why - that is what it is for.',
    goBack: 'Go back',
    youAreLeaving: 'You are leaving',
    thisJob: 'this job',
    reasonPlaceholder: 'What happened? Even a few lines helps somebody understand it.',
    tooShort: 'Please write a little more - it is what a moderator has to work from.',
  },


  /* ---------------------------------------------------------------
     A STUDENT'S OWN AVAILABILITY, AND REPORTING SOMEBODY
     --------------------------------------------------------------- */
  myOffer: {
    addAnother: 'Add another offer',
    editOffer: 'Edit this offer',
    noneYet: 'You have not put anything up yet. An offer is how people find you rather than you finding them.',
    removeWindow: 'Remove {from} to {to} on {day}',
    takenDownBadge: 'Taken down',
    takenDownTitle: 'A moderator took this down.',
    weekIsShared: 'Your times are set once and shared by all your offers - you do not set them again here.',
    weekSaved: 'Your week has been saved.',
    whichTown: 'Which town',
    yourOffers: 'Your offers ({count} of {max})',
    loading: 'Loading your offer...',
    title: 'My offer',
    subtitle: 'Say what you can do and when you are usually free, so people can come to you.',
    yours: 'Your offer',
    create: 'Create your offer',
    whatCanYouDo: 'What can you help with?',
    pickFive: 'Pick up to five.',
    aboutYou: 'A little about what you do',
    aboutPlaceholder: 'I babysit for neighbours most weekends and I am good with younger children...',
    // Shown next to the character count WHILE they type, and only while they
    // are under it. The number comes from the server - see routes/offers.js.
    atLeastChars: 'at least {count} characters',
    town: 'Town you can work in',
    howFar: 'How far can you travel?',
    doNotMind: 'I do not mind',
    upToKm: 'Up to {km} km',
    availableOn: 'Available on:',
    nothingSet: 'Nothing set yet.',
    publish: 'Publish my offer',
    saveChanges: 'Save changes',
    takeDownQ: 'Take your offer down?',
    saved: 'Saved. This is your week from now on, until you change it.',
    accepted: 'Accepted. It is now in your work, with the address.',
    declined: 'Declined. They have been told, and not why.',
    takenDown: 'Your offer has been taken down. Anything already agreed still stands.',
    fillBoth: 'Fill in both times.',
    finishAfterStart: 'The finish time has to be after the start.',
    underMinutes: 'That is under {count} minutes.',
    longerThanHours: 'That is longer than {count} hours.',
    overlaps: 'That overlaps {from}-{to}.',
    askedToBookYou: { one: '{count} person has asked to book you', other: '{count} people have asked to book you' },
    nothingBooked: 'Nothing is booked until you accept. You do not have to give a reason for saying no.',
    separateTimes: '{count} separate times',
    eachAmount: '{amount} each',
    inAll: '{amount} in all',
    until: 'until {time}',
    yesAgreesAll: 'Saying yes agrees to all of them. If most of these work but one does not, say no and tell them - they can ask again for the days that suit you. Once it is booked you can still drop a single day, up to 24 hours before it.',
    fullAddressOnAccept: 'You will get the full address once you accept.',
    accept: 'Accept',
    noThanks: 'No thanks',
    live: 'Live',
    paused: 'Paused',
    pause: 'Pause',
    resume: 'Resume',
    notAnAgreement: 'Putting times here does not agree to anything. If somebody wants one of them they have to ask you first, and you will see what the job is and what it pays before you decide.',
    noPersonalDetails: 'Do not put your address, phone number or school year group in here - it is public to everyone who can see your offer.',
    whenUsuallyFree: 'When are you usually free?',
    startTimeOn: 'Start time on {day}',
    finishTimeOn: 'Finish time on {day}',
    from: 'From',
    to: 'To',
    toWord: 'to',
    add: 'Add',
    saveMyWeek: 'Save my week',
    resetToUsual: 'Reset to the usual',
    takeItDown: 'Take it down',
    keepItUp: 'Keep it up',
    takeDownBody: 'You will stop appearing when people look for someone who is free. Anything you have already agreed to still stands - those are in your work and have not changed.',
  },

  report: {
    button: 'Report',
    emergency: 'If anyone is in immediate danger, call 112 first.',
    emergencyBody: 'Then tell us, so we can act.',
    whatHappened: 'What happened?',
    chooseOne: 'Choose one...',
    anythingToAdd: 'Anything you want to add',
    choosePlease: 'Please choose what happened.',
    r1: 'They made me feel uncomfortable or unsafe',
    r2: 'Inappropriate behaviour or messages',
    r3: 'I was asked to do something unsafe',
    r4: 'They did not pay what was agreed',
    r5: 'They did not turn up',
    r6: 'The profile does not seem real',
    r7: 'An abusive or unfair review',
    r8: 'Something else',
    thisPerson: 'this person',
    reportName: 'Report {name}',
    blockButton: 'Block',
    blockName: 'Block {name}',
    blockNameQ: 'Block {name}?',
    thankYou: 'Thank you',
    close: 'Close',
    sending: 'Sending...',
    send: 'Send report',
    detailsPlaceholder: 'You do not have to write anything, but it helps us understand.',
    realPerson: 'A real person reads every report. We will not tell them who reported it.',
    blocked: 'Blocked',
    blocking: 'Blocking...',
    blockThem: 'Block them',
    blockBody: 'You will not see {name} or their chores again, and they will not see you. You do not have to give a reason, and they are not told.',
    blockUndo: 'You can undo this at any time from your account settings. If something happened that we should know about, please report it as well.',
  },


  daypicker: {
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    chooseDays: 'Choose the days',
  },


  /* ---------------------------------------------------------------
     THE SETTINGS PAGE, AND THE LAST OF THE MARKETPLACE FILTERS
     --------------------------------------------------------------- */
  settingsPage: {
    title: 'Your account',
    dateOfBirth: 'Date of birth',
    dateOfBirthIs: '{date} - you are {age}',
    dateOfBirthFixed: 'Others only see your age, which updates on your birthday. If this date is wrong, tell us through "Report an issue".',
    dateOfBirthAdd: 'Add it once and your age will update by itself on your birthday. It has to match the age already on your account.',
    viewProfile: 'View public profile',
    yourDetails: 'Your details',
    detailsSaved: 'Your details have been saved.',
    changePhoto: 'Change photo',
    addPhoto: 'Add a photo',
    photoUpdated: 'Photo updated.',
    photoRemoved: 'Photo removed.',
    photoFailed: 'That photo could not be uploaded. Please try a different one.',
    firstName: 'First name',
    lastName: 'Last name',
    phone: 'Phone number',
    school: 'School',
    schoolYear: 'School year',
    aboutYou: 'About you',
    aboutPlaceholder: 'What are you good at? When are you usually free?',
    happyToDo: 'What you are happy to do',
    howFar: 'How far will you travel?',
    doNotMind: 'I do not mind',
    upToKm: 'Up to {km} km',
    whereYouAre: 'Where you are',
    saveChanges: 'Save changes',
    membership: 'Membership',
    active: 'Active',
    notActive: 'Not active',
    manage: 'Manage',
    join: 'Join for €5/year',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    atLeast8: 'At least 8 characters.',
    updatePassword: 'Update password',
    passwordUpdated: 'Password updated.',
    blocked: 'People you have blocked',
    closeAccount: 'Close your account',
    closing: 'Closing...',
    closeForever: 'Yes, close it permanently',
    cannotUndo: 'This cannot be undone. You will not be able to log in again.',
    whatGoes: 'What gets removed:',
    whatStays: 'What stays:',
    confirmPassword: 'Enter your password to confirm',
    removePhoto: 'Remove photo',
    age: 'Age',
    travelHint: 'Leave it as “I do not mind” unless you really have a limit.',
    needMembership: 'You need an active membership to post chores.',
    noBlocks: 'You have not blocked anybody. If somebody makes you uncomfortable, you can block them from their profile - they are never told.',
    unblock: 'Unblock',
    closeAccountBody: 'This removes your name, email, and everything else personal, and cannot be undone.',
    closeMyAccount: 'Close my account',
    closeAccountQ: 'Close your account?',
    keepMyAccount: 'Keep my account',
    whatStaysBody: 'the reviews you wrote, the star ratings you gave, and the chores you completed - all shown as “Deleted user” with no link back to you. We keep these so the people you worked with do not lose their review history. They contain nothing that identifies you.',
    openWillBeCancelled: 'Anything still open will be cancelled, and the other person will be told.',
    photoNotUploading: 'Photo not uploading? Read this',
    photoInICloudQ: 'First, and most likely: is the photo in iCloud rather than on this device?',
    photoSignIs: 'The sign is this:',
    photoTapAndI: 'you tap the photo you want and an ⓘ appears on it',
    photoSignBody: '- a letter i in a circle, on the picture itself. It is the same symbol the Photos app shows when a picture is in iCloud and it cannot fetch it. Your device can show you the thumbnail but cannot hand the real file over, so nothing arrives here and nothing happens.',
    photoFixLabel: 'The fix:',
    photoFixBefore: 'check you are on wi-fi or have a signal - it has to download the photo to do this. Then open the',
    photoAppName: 'Photos',
    photoFixAfter: 'app, tap the picture you want, and wait for it to finish downloading; it goes from soft to sharp and the ⓘ disappears. Come back here and it will upload straight away.',
    photoBrowseBefore: 'On an iPhone or iPad this also offers',
    photoBrowseName: 'Browse',
    photoBrowseAfter: ', which opens Files instead of the photo library. Saving the photo to Files first and picking it from there avoids the photo library completely.',
    backToDashboard: 'Back to my dashboard',
    signedInAs: 'Signed in as {email}',
    addAPhoto: 'Add a photo',
    working: 'Working...',
  },

  marketplaceFilters: {
    payFrom: 'Pay from (€)',
    payUpTo: 'Pay up to (€)',
    anyDistance: 'Any distance',
    searchPlaceholder: 'lawn, dog walking, tutoring...',
    any: 'Any',
    within: 'Within',
    search: 'Search',
    closestToMe: 'Closest to me',
    toSortByDistance: 'to sort by distance and filter by km.',
    clearFilters: 'Clear filters',
    addYourAddress: 'Add your address',
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {page} of {pages}',
  },


  /* ---------------------------------------------------------------
     THE ONE-TIME NOTIFICATION ASK, AND A MODERATOR'S RECOVERY CODES
     --------------------------------------------------------------- */
  notifyPrompt: {
    titleStudent: 'Want to know when a job comes up?',
    titlePoster: 'Want to know when somebody applies?',
    notNow: 'Not now',
    justAMoment: 'Just a moment...',
    yesTellMe: 'Yes, tell me',
    doneStudent: 'You will get a notification when a job you would want is posted. You can change what you hear about, or turn it off, in Settings.',
    donePoster: 'You will get a notification when somebody applies to one of your chores. You can turn it off again in Settings.',
    pitchStudent: 'Jobs go quickly, and the students who get them are usually the ones who hear first. We can send a notification to your phone the moment one is posted that suits you - on the lock screen, without you having to keep checking.',
    reassureStudent: 'You choose what you hear about, there is nothing at night, and you can turn it off whenever you like.',
    reassurePoster: 'Nothing at night, and you can turn it off whenever you like.',
    noEmail: 'We will not email you about jobs.',
    allSet: 'All set',
    offBody: 'No problem. You can turn notifications on at any time from Settings - nothing else changes.',
    pitchPoster: 'A chore with nobody on it is usually one where the first person to apply never heard back. We can tell you the moment somebody applies, so you can accept them while they are still free.',
    waitingOnParent: 'You can turn this on now - alerts will start once your parent or guardian has confirmed your account.',
  },

  twoFactor2: {
    recoveryCodes: 'Recovery codes',
    noneLeft: 'You have no recovery codes left. If your email is ever unreachable you will not be able to moderate.',
    fewLeft: { one: 'Only {count} recovery code left. Generate a new set.', other: 'Only {count} recovery codes left. Generate a new set.' },
    yourPassword: 'Your password',
    generating: 'Generating...',
    generateNew: 'Generate a new set',
    oldStop: 'Any codes you already have will stop working.',
    title: 'Two-step login',
    whyBefore: 'Because this account can read reported conversations, logging in takes a password',
    and: 'and',
    whyAfter: 'a six-digit code. We email the code to',
    eachTime: 'each time.',
    lastLogin: 'Last two-step login: {date}. If that was not you, change your password now.',
    eachLogsInOnce: 'Each one logs you in once, without email. They are what gets you in when the code cannot reach you - so keep them somewhere that is',
    not: 'not',
    yourInbox: 'your inbox.',
    unusedIssued: '{count} unused, issued {date}.',
    unusedOnly: '{count} unused.',
    writeThemDown: 'Write these down before you leave this page. They are not saved anywhere we can read, so they cannot be shown again.',
    passwordWhy: 'Asked for because a new set is ten new ways into this account.',
  },


  /* ---------------------------------------------------------------
     JOB ALERTS, AND PUTTING THE SITE ON A PHONE

     The install steps name buttons exactly as the device labels them -
     somebody following this is looking at a menu and matching words. The
     Italian uses what an Italian-language phone actually says.
     --------------------------------------------------------------- */
  prefs: {
    title: 'Notifications',
    subtitle: 'What we tell you about, and what we leave alone.',
    everything: 'Notify me about things on ChoreConnect',
    everythingHint: 'Turn this off and nothing will buzz your phone or fill the bell.',
    messages: 'When somebody messages me',
    messagesHint: 'Your unread messages still show on the speech bubble either way - this is only about being interrupted.',
    offWarning: 'Nothing will buzz your phone. We will still put a few things in the bell that you would be worse off not knowing - a job you are booked on being cancelled, or something of yours being taken down.',
  },

  alerts: {
    title: 'Job alerts',
    onHere: 'On here',
    offHere: 'Off here',
    on: 'Notifications are on for this device.',
    off: 'Notifications are off for this device.',
    testSent: 'Sent. It should appear in a second or two.',
    failed: 'That did not work.',
    refused: 'Your browser refused. You can allow notifications again from the padlock icon in the address bar.',
    blocked: 'Notifications are blocked for this site. Allow them from the padlock icon in the address bar, then try again.',
    iosNeedsInstall: 'On an iPhone or iPad you have to add ChoreConnect to your home screen first - see the steps below.',
    serverOff: 'The server is not set up to send notifications yet.',
    unsupported: 'Not available in this browser yet - on an iPhone or iPad, add ChoreConnect to your home screen first. See the steps below.',
    unavailableHere: 'Notifications are not available in this browser.',
    pitch: 'Get a notification on your phone the moment a job you would want is posted - on the lock screen, with a sound, without opening the site. We will not email you about jobs.',
    addToHomeScreen: 'Add to Home Screen',
    iosStepsBefore: 'Tap the share button in Safari, choose',
    iosStepsAfter: ', then open ChoreConnect from there. Apple only allows notifications for sites added that way - it is not something we can switch on for you.',
    commonestReason: 'The commonest reason by far is that ChoreConnect has not been added to your home screen yet.',
    appleNoSafari: '- Apple does not allow notifications for a site opened in Safari, and there is no setting anywhere that changes that.',
    deviceList: '- iPhone and iPad, Android, or computer and Chromebook.',
    blockedBody: 'We cannot ask again from here - tap the padlock (or the icon next to the address) in your browser, allow notifications for ChoreConnect, then reload this page.',
    turnOffHere: 'Turn off on this device',
    sendTest: 'Send me a test',
    justAMoment: 'Just a moment...',
    turnOn: 'Turn on notifications',
    perDevice: 'This is per device. Turning it on here does not turn it on for your laptop, and turning it off here leaves your other devices alone.',
    onlyTheseKinds: 'Only these kinds of job',
    nothingChosen: 'Nothing chosen here, so we use the kinds of work on your profile. Pick some to narrow it further.',
    chosenCount: '{count} chosen.',
    onlyIfPays: 'Only if it pays at least',
    anyAmount: 'Any amount',
    onlyWithin: 'Only within',
    anyDistance: 'Any distance',
    quietFrom: 'Do not disturb from',
    quietUntil: 'until',
    saveWhatIWant: 'Save what I want to hear about',
    saved: 'Saved.',
  },

  install: {
    chooseToSeeHow: 'Tap your device above and we will show you how - it takes two taps and about a minute.',
    whichDevice: 'Which device are you setting up?',

    deviceIos: 'iPhone or iPad',
    deviceAndroid: 'Android phone',
    deviceDesktop: 'Computer or Chromebook',
    addToHome: 'Add ChoreConnect to your home screen',
    desktopTitle: 'ChoreConnect on a computer or Chromebook',

    iosWhy: 'On an iPhone or iPad this is the ONLY way to get notifications. Apple does not allow them for a site opened in the browser, so this step has to come first. It is not something we can switch on for you.',
    iosNote: 'It has to be Safari. Adding it from Chrome on an iPhone or iPad will not work for notifications.',
    ios1: 'Open ChoreConnect in Safari.',
    ios2: 'Tap the Share button - on an iPhone it is at the bottom of the screen, on an iPad it is at the top right. It is the square with an arrow pointing up out of it.',
    ios3: 'Scroll down the list and tap "Add to Home Screen".',
    ios4: 'Tap "Add" in the top right.',
    ios5: 'Close Safari and open ChoreConnect from your home screen instead. It has to be opened from there from now on.',
    ios6: 'Go to Settings inside the app and turn notifications on. iOS will ask you to allow them - tap "Allow".',

    androidWhy: 'Notifications work on Android either way, so this one is a convenience rather than a requirement - it opens full screen, like an app, with no address bar.',
    androidNote: 'If you are not asked to allow notifications, check Android Settings, then Apps, then ChoreConnect or Chrome, then Notifications.',
    android1: 'Open ChoreConnect in Chrome.',
    android2: 'Tap the three dots at the top right.',
    android3: 'Tap "Add to Home screen" (some phones call it "Install app").',
    android4: 'Tap "Install" to confirm.',
    android5: 'Go to Settings inside ChoreConnect and turn notifications on. Chrome will ask - tap "Allow".',

    desktopWhy: 'Notifications already work in an ordinary browser window here - installing is optional and only makes it open in its own window.',
    desktopNote: 'Safari on a Mac does not support these notifications at all. Use Chrome, Edge or Firefox on a computer.',
    desktop1: 'Go to Settings inside ChoreConnect and turn notifications on.',
    desktop2: 'Your browser will ask at the top of the screen - click "Allow". If you miss it, click the padlock next to the address and allow notifications there.',
    desktop3: 'To install it as well (optional): look for the install icon at the right-hand end of the address bar - a screen with an arrow, or a plus - then click "Install".',
    desktop4: 'On a Chromebook it is the same, and it then appears in your launcher with your other apps.',
    getOnYourPhone: 'Get ChoreConnect on your phone',
    twoMinutesOnce: 'Two minutes, once. It opens like an app, and it is how you find out about things without having to keep checking.',
    nothingToDo: 'Nothing to do here - you are opening ChoreConnect the right way.',
    onForThisDevice: 'On for this device. You can turn them off again here whenever you like.',
    buttonAppearsWhen: 'The button appears once ChoreConnect is on your home screen and you have opened it from there. This is Apple’s rule, not ours - there is no way for us to switch it on for you.',
    blockedBodyShort: 'We cannot ask again from here - tap the padlock next to the address, allow notifications, then reload.',
    phoneWillAsk: 'Your phone will ask you to confirm. Nothing at night, and you can turn it off at any time.',
    perDeviceShort: 'This is per device. Doing it on your phone does not do it on your laptop.',
    notificationsOn: 'Done. Notifications are on for this device.',
  },


  /* ---------------------------------------------------------------
     MODERATOR DIALOGS, MEMBERSHIP AND DONATIONS
     --------------------------------------------------------------- */
  suspend: {
    needReason: 'Please say why. They are shown this when they try to log in.',
    badLength: 'Choose a length between 1 and 365 days, or indefinitely.',
    suspending: 'Suspending...',
    indefinitely: 'Suspend indefinitely',
    forDays: 'Suspend for {days} days',
    howLong: 'How long',
    numberOfDays: 'Number of days',
    whyTheyRead: 'Why - they will read this',
    titleFor: 'Suspend {name}?',
    thisPerson: 'this person',
    signedOutEverywhere: 'They will be signed out on every device straight away, and cannot log in until it ends.',
    custom: 'Custom',
    reasonPlaceholder: 'Written to them, not about them. Say what happened and what needs to change.',
    reasonHint: 'Shown word for word every time they try to log in. Up to 500 characters.',
    indefiniteWarning: 'An indefinite suspension does not end on its own. They are told it is not temporary.',
  },

  takedown: {
    needReason: 'Please say why. They are sent this, word for word.',
    titleOffer: "Take down {name}'s availability?",
    titleChore: 'Take down this chore?',
    takingDown: 'Taking down...',
    takeItDown: 'Take it down',
    effectOffer: 'They come off the list of available students straight away. Nobody can ask to book them until they publish again.',
    effectChore: 'It comes off the marketplace straight away. Nobody new can apply for it.',
    whyTheyRead: 'Why - they will read this',
    hintOffer: 'Written to them. Say what was wrong and what would be alright.',
    hintChore: 'Written to them. Say what was wrong with the wording and what would be alright.',
    reasonHint: 'Sent to them as a notification, word for word. Up to 500 characters.',
    nothingDeletedBefore: 'Nothing is deleted. If this turns out to be wrong, it can be put back from the',
    takenDownTab: 'Taken down',
    nothingDeletedAfter: 'tab, and they are told about that too.',
  },

  subscribe: {
    freeTitle: 'ChoreConnect is free',
    canDoEverything: 'Your account can already do everything',
    thanks: 'Thank you! Your membership is now active.',
    cancelled: 'Payment cancelled - nothing was charged.',
    devOn: 'Membership switched on in development mode. No payment was taken.',
    perk1: 'Post as many chores as you like, all year',
    perk2: 'See every student who applies, with their reviews',
    perk3: 'Accept one student and name a backup',
    perk4: 'Star ratings and written reviews both ways',
    perk5: 'Email and in-app notifications',
    nothingToPay: 'There is nothing to pay, and nothing to set up.',
    postAChore: 'Post a chore',
    keepingFree: 'ChoreConnect is free, and there is no plan to change that. If it ever had to, everybody would hear about it a long way in advance - nobody would find out from a bill.',
    membershipTitle: 'ChoreConnect membership',
    membershipSubtitle: '€5 a year to post chores. Students never pay a penny.',
    runsUntil: 'Runs until',
    manageOrCancel: 'Manage or cancel membership',
    perYear: 'per year',
    stripeHandles: 'Payment is handled by Stripe. Your card details never touch this site.',
    studentBefore: 'Are you a student? You never need this -',
    goFindWork: 'go and find work',
  },

  support: {
    title: 'Keeping ChoreConnect running',
    subtitle: 'What it costs, and how you can help if you want to.',
    thanks: 'Thank you.',
    whereMoneyGoes: 'Where the money goes',
    chipIn: 'Chip in',
    noPaymentTaken: 'No payment was taken. No hard feelings at all - the site works exactly the same.',
    notACompany: 'ChoreConnect is not a company and nobody takes a wage from it. It is free for the people posting chores and free for the students, and the cash for each job passes directly between the two of them - the site never touches it.',
    whatItCosts: 'What it does cost is keeping the thing online: the domain name, the server, and the email that sends parents their consent requests. Donations go on that, and nothing else.',
    oneOffNoBadge: 'One-off, never a subscription - there is nothing to cancel later. Payment is handled by Stripe and your card details do not touch this site. Giving buys nothing: no badge, no priority, and nothing any other person can see.',
    notSwitchedOn: 'Donations are not switched on at the moment. The site is running fine without them.',
    thanksBody: 'Your {amount} goes towards this year’s costs. Stripe will email you a receipt.',
    notConfirmedYet: 'Stripe has not confirmed that payment yet. If it goes through, Stripe will email you a receipt.',
    otherAmount: 'Another amount (€)',
    otherAmountHint: 'Anything from {min} to {max}.',
    otherAmountInvalid: 'Enter an amount from {min} to {max}.',
    give: 'Give',
    devNoStripe: 'Card payments are not set up on this computer, so these buttons will not work. Add STRIPE_SECRET_KEY to backend/.env.',
  },


  /* ---------------------------------------------------------------
     MODERATION - the queue, the log, and what is currently hidden
     --------------------------------------------------------------- */
  moderation: {
    tabIssues: 'Issues',
    issuesEmptyTitle: 'Nobody has reported a problem',
    issuesEmptyBody: 'When somebody tells us something is broken, their conversation appears here.',
    issuesBackToList: 'Back to the list',
    issuesNothingYet: 'Nothing in this conversation yet.',
    issuesReply: 'Your reply',
    issuesReplyPlaceholder: 'Answer them...',
    issuesSend: 'Reply',
    issuesYou: 'You:',
    issuesMinor: 'aged {age}, a minor',
    issuesClosedAccount: 'account closed',
    issuesAnonymousNote:
      'They see this as coming from "Moderator". Your name, photograph and profile are never shown here.',
    emailWhat: 'What it does',
    emailOnTitle: 'Emails are being sent normally',
    emailOnBody:
      'Turn this off when the day’s 300 have gone. While it is off, signing up, confirming an address, resetting a password and asking a parent for consent are all refused with an explanation instead of failing silently.',
    emailOffTitle: 'Emails are switched OFF',
    emailOffBody:
      'Nobody can sign up, reset a password, confirm an address, or have a consent request sent to a parent. Everyone sees a notice saying so. Turn this back on as soon as the quota resets.',
    emailTurnOff: 'Switch emails off',
    emailTurnOn: 'Switch emails back on',
    emailNowOff: 'Emails are off. Everyone is being told, and nothing will be sent.',
    emailNowOn: 'Emails are back on.',
    title: 'Moderation',
    loading: 'Loading reports...',
    notFound: 'Page not found',
    nothingHere: 'There is nothing here.',
    nothingWithStatus: 'Nothing here',
    noReportsWithStatus: 'No reports with that status.',
    whatDidYouDecide: 'What did you decide?',
    tabOpen: 'Open',
    tabInvestigating: 'Looking into it',
    tabResolved: 'Resolved',
    tabDismissed: 'Dismissed',
    tabAudit: 'Audit log',

    reasonInappropriate: 'Inappropriate behaviour',
    reasonUnsafe: 'Made them feel unsafe',
    reasonUnsafeWork: 'Asked to do something unsafe',
    reasonNoShow: 'Did not turn up',
    reasonFake: 'Profile may be fake',
    reasonAbusiveReview: 'Abusive review',
    reasonOther: 'Something else',

    openingConversation: 'Opening the conversation...',
    offerConversation: 'A question about a student’s offer',
    questionNotBooked: 'A question asked before applying. This student was not booked for the job.',
    questionBadge: 'Question before applying',
    offerGone: 'the offer has been taken down',
    offerStillUp: 'the offer is still up',
    noConversation: 'There is no conversation here.',
    readingPrivate: 'You are reading a private conversation between an adult and a student.',

    loadingTakedowns: 'Loading takedowns...',
    nothingTakenDown: 'Nothing is taken down',
    reasonGiven: 'Reason given:',
    offMarketplace: 'Off the marketplace',
    offWhosFree: 'Not listed as available',
    availability: 'Availability',

    readingLog: 'Reading the log...',
    nothingRecorded: 'Nothing recorded',
    noEntriesMatch: 'No entries match that.',
    theSiteItself: 'The site itself',
    logEverything: 'Everything',
    logConversationsRead: 'Conversations read',
    logSuspensions: 'Suspensions',
    logReports: 'Reports',
    logCancellations: 'Cancellations',
    logConsentGiven: 'Consent given',
    backToList: 'Back to the list',
    poster: 'Poster',
    student: 'Student',
    fromStudent: 'student',
    fromPoster: 'poster',
    contactDetails: 'contact details',
    hidden: 'hidden',
    putItBack: 'Put it back',
    hideFromBoth: 'Hide from both of them',
    hidingNote: 'Hiding takes a message off the screen for the two people. The text stays on file - it is evidence, and somebody who could delete what they sent could delete the proof of it.',
    onlyThreadsBefore: 'Only conversations that were',
    reported: 'reported',
    onlyThreadsMiddle: ', or where somebody',
    sharedContact: 'shared a phone number or email',
    onlyThreadsAfter: '. Everything else is private, and the server refuses it even with a direct link.',
    nothingToLookAt: 'Nothing to look at',
    nothingToLookAtBody: 'No conversation has been reported, and nobody has swapped contact details. That is the result you want.',
    urgentReport: 'urgent report',
    openReports: { one: '{count} open report', other: '{count} open reports' },
    choreLabel: 'Chore',
    auditNote: 'Newest first. Nothing here is ever edited or removed - that is the point of it.',
    recorded: 'Recorded.',
    postedBy: 'Posted by',
    posterTag: '(poster) and',
    studentTag: '(student',
    handled: '{count} handled',
    withContactDetails: '{count} with contact details',
    urgent: 'Urgent',
    outcomeLabel: 'Outcome:',
    takeDownChore: 'Take down chore',
    takeDownOffer: 'Take down availability',
    suspend: 'Suspend',
    reinstate: 'Reinstate',
    recordDecision: 'Record decision',
    lookingIntoIt: 'Looking into it',
    dismiss: 'Dismiss',
    resolved: 'Resolved',
    outcomePlaceholder: 'Spoke to both, no further action. / Suspended pending contact with the school.',
    outcomeHint: 'Write this even when you decide to do nothing. It is the record that you looked.',
    takedownsEmptyBody: 'Chores and offers a moderator has removed appear here, with the reason given, so they can be put back.',
    tabReports: 'Reports',
    tabTakenDown: 'Taken down',
    readConversation: 'Read the conversation',
    aboutLabel: 'About:',
    aChore: 'a chore',
    fromWhom: 'From {name}',
    agedYears: ' (aged {age})',
    statusLabel: 'Status: {status}',
    urgentWaiting: { one: '{count} urgent report waiting.', other: '{count} urgent reports waiting.' },
    openWaiting: { one: '{count} open report.', other: '{count} open reports.' },
    call112: 'If anyone may be in immediate danger, call 112 before doing anything here.',
    flaggedStrip: { one: '{count} conversation flagged by the contact-details detector', other: '{count} conversations flagged by the contact-details detector' },
    flaggedStripBody: 'Nobody reported these. They are here because a phone number or email was shared, which is the pattern nobody reports.',
  },


  /* The tab strips on the two dashboards. Separate from `status.*` because a
     tab is a heading over a list, not a badge on one thing - "Booked in"
     reads better above a column than "Booked" does. */
  tabs: {
    open: 'Open',
    expired: 'Expired',
    bookedIn: 'Booked in',
    completed: 'Completed',
    gotTheJob: 'Got the job',
    backup: 'Backup',
    waitingToHear: 'Waiting to hear',
    noneOfThese: 'No {kind} chores',
  },

  donate: {
    title: 'ChoreConnect runs on donations',
    notJustNow: 'Not just now',
    niceOne: 'Nice one - that is another job done. ChoreConnect is free to use and there is no company behind it, but it does cost money to keep online: hosting, the domain and the email that sends parents their consent requests.',
    takenToStripe: 'You will be taken to Stripe to pay.',
    otherAmountLink: 'Another amount',
    runsOnDonations: 'It runs on donations from the people who use it. If you can spare something towards this year’s costs, it genuinely helps.',
    oneOff: 'One-off, never a subscription. Payment is handled by Stripe - your card details do not touch this site. Donating changes nothing about how ChoreConnect works for you or for the students: it buys no priority, no badge and nothing anyone else can see.',
  },


  /* The last of it - odds and ends the screen-by-screen passes missed. */
  time: {
    // The word joining a date to a clock time. Italian uses "alle" and it has
    // to agree with the sentence around it, which is exactly why this is a
    // phrase rather than a word written into utils/schedule.js.
    dateAtTime: '{date} at {time}',
    // A chore whose poster does not mind what time of day.
    flexible: 'flexible time',
    dateFlexible: '{date}, at a time that suits you both',
    justNow: 'just now',
    minutesAgo: { one: '{count} minute ago', other: '{count} minutes ago' },
    hoursAgo: { one: '{count} hour ago', other: '{count} hours ago' },
    daysAgo: { one: '{count} day ago', other: '{count} days ago' },
    monthsAgo: { one: '{count} month ago', other: '{count} months ago' },
    yearsAgo: { one: '{count} year ago', other: '{count} years ago' },
    inThePast: 'in the past',
    inMinutes: { one: 'in {count} minute', other: 'in {count} minutes' },
    inHours: { one: 'in {count} hour', other: 'in {count} hours' },
    inDays: { one: 'in {count} day', other: 'in {count} days' },
  },

  misc: {
    reportedByMinor: 'Reported by a minor',
    alreadySuspended: 'Already suspended',
    whatYouDid: 'What you did, and why',
    whyYouCanSee: 'Why you can see this',
    nothingSent: 'Nothing was ever sent.',
    membershipActive: 'Your membership is active',
    cardsOff: 'Card payments are not switched on yet.',
    alreadyInstalled: 'Already on your home screen.',
    alreadyInstalledBody: 'Nothing to do here - you are opening ChoreConnect the right way.',
    allowNotifications: 'Allow notifications',
    blockedHere: 'Notifications are blocked for this site.',
    browserCannot: 'This browser cannot show notifications.',
    iosFirstStep: 'One step first on an iPhone or iPad.',
    fullSteps: 'Full steps for your device',
    stepByStep: 'Step-by-step instructions for your device',
    tellMeNewJob: 'Tell me when a new job is posted',
    mustBeInstalled: 'On an iPhone or iPad it has to be',
    twoMinutes: 'It takes about two minutes.',
    plainWay: 'Still not working? Try the plain way.',
    someone: 'Someone',
    deletedUser: 'Deleted user',
    // "From home", not "away from you" (the owner, 18 September 2026): what
    // is measured is the address on the account to the address of the job,
    // and neither end is wherever the phone happens to be standing.
    metresAway: '{count} m from home',
    kmAway: '{count} km from home',
    // Road distances (utils/format.js, travel). "By road" is as a scooter or
    // car goes, without motorways; "on foot" is the walk.
    kmByRoad: '{count} km from home by road',
    mByRoad: '{count} m from home by road',
    kmOnFoot: '{count} km on foot',
    mOnFoot: '{count} m on foot',
    // Only when the route planner could not answer.
    kmStraight: '{count} km from home in a straight line',
    mStraight: '{count} m from home in a straight line',
    accountClosed: 'This account has been closed',
    starRating: 'Star rating',
    viewDetails: 'View details',
    removeNotification: 'Remove notification',
  },

  footer: {
    reportIssue: 'Report an issue',
    tagline: 'Connecting local people who need chores done and students who would like to earn. Fair pay, real reviews, and a bit of independence.',
    forStudents: 'For students',
    forPosters: 'For posting chores',
    browse: 'Browse chores',
    createAccount: 'Create an account',
    membership: 'Membership',
    postChore: 'Post a chore',
    privacy: 'Privacy',
    terms: 'Terms',
    builtFree: 'Built for students. Free for everybody.',
    builtPaid: 'Built for students. €5 a year for people posting chores, always free to earn.',
  },
};
